/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: branding-asset-service.ts
 *
 * Created by kyang on 10/21/20.
 */

// This service is an interface for all the container type-specific asset services.

angular.module('prism-ui.components.branding').service('BrandingAssetService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    ContentResourceFactory: JS5Object,
    NetworkResourceFactory: JS5Object,
) {

    //========================//
    // Abstract Functionality //
    //========================//

    let assetServiceImplementations: JS5Object = {};

    this.registerAssetServiceImplementation = (containerType: string, service: JS5Object) => {
        if (_.has(assetServiceImplementations, containerType)) {
            console.warn(`Replacing previous BrandingAssetService implementation for container type: ${containerType}`);
        }
        assetServiceImplementations[containerType] = service;
    };

    this.getAssetServiceImplementation = (containerType: string) => {
        return assetServiceImplementations[containerType];
    };


    //==================================//
    // Abstract Container Functionality //
    //==================================//

    this.getBrandingAssetContainer = (brandingConfig: JS5Object) => {
        if (brandingConfig.id > -1) {
            return this.getAssetServiceImplementation(brandingConfig.type)
                .getBrandingAssetContainer(brandingConfig.id);
        }
        return $q.when({});
    };

    this.parseBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        return this.getAssetServiceImplementation(brandingConfig.type)
            .parseBrandingAssetContainer(brandingConfig, brandingAssetContainer);
    };

    this.updateBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        return this.getAssetServiceImplementation(brandingConfig.type)
            .updateBrandingAssetContainer(brandingConfig, brandingAssetContainer);
    };


    //==============================//
    // Abstract Asset Functionality //
    //==============================//

    this.getRootBrandingAsset = (brandingConfig: JS5Object, container: JS5Object, acceptProfile: JS5Object) => {
        return this.getAssetServiceImplementation(brandingConfig.type)
            .getRootBrandingAsset(brandingConfig, container, acceptProfile);
    };

    this.validateNonTextAsset = (containerType: string, acceptProfileId: number, asset: JS5Object, virtualPath: string) => {
        return this.getAssetServiceImplementation(containerType)
            .validateNonTextAsset(acceptProfileId, asset, virtualPath);
    };

    this.createRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        let savePayload = _.cloneDeep(rootAsset);
        if (_.get(savePayload, 'language.id') === -1) {
            savePayload.language = null;
        }
        return this.getAssetServiceImplementation(brandingConfig.type)
            .createRootBrandingAsset(brandingConfig, savePayload);
    };

    this.updateRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        let savePayload = _.cloneDeep(rootAsset);
        if (_.get(savePayload, 'language.id') === -1) {
            savePayload.language = null;
        }
        return this.getAssetServiceImplementation(brandingConfig.type)
            .updateRootBrandingAsset(brandingConfig, savePayload);
    };


    //==============================//
    // Abstract Proxy Functionality //
    //==============================//

    enum ProxyStatus {
        PROXY_COMPLETE = 'COMPLETED',
        PROXY_STALLED = 'STALLED',
        PROXY_RUNNING = 'RUNNING',
    }
    this.ProxyStatus = ProxyStatus;

    this.hasVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        return this.getAssetServiceImplementation(brandingConfig.type)
            .hasVideoAssetProxyStatus(brandingConfig, rootAsset);
    };

    // Function is invoked by BrandingViewService.createRootAssetProxyNotifications()
    this.updateVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object, intervalData: JS5Object) => {
        return this.getAssetServiceImplementation(brandingConfig.type)
            .updateVideoAssetProxyStatus(brandingConfig, rootAsset, intervalData);
    };


    //==========================================//
    // Branding Container Owner Implementations //
    //==========================================//

    let BRANDING_ASSET_CONTAINER_OWNERS: JS5Object = {
        networks: {
            getBrandingAssetContainers: NetworkResourceFactory.getBranding,
            createBrandingAssetContainer: NetworkResourceFactory.saveBranding,
            toggleBrandingAssetContainerVisibility: NetworkResourceFactory.delete,
        },
        series: {
            getBrandingAssetContainers: ContentResourceFactory.getBranding,
            createBrandingAssetContainer: ContentResourceFactory.saveBranding,
            toggleBrandingAssetContainerVisibility: ContentResourceFactory.delete,
        },
        movies: {
            getBrandingAssetContainers: ContentResourceFactory.getBranding,
            createBrandingAssetContainer: ContentResourceFactory.saveBranding,
            toggleBrandingAssetContainerVisibility: ContentResourceFactory.delete,
        }
    };

    this.getBrandingAssetContainers = (brandingConfig: JS5Object, ownerId: string, includeHidden: boolean) => {
        let containerOwnerService = BRANDING_ASSET_CONTAINER_OWNERS[brandingConfig.groupName];
        return containerOwnerService.getBrandingAssetContainers({
            fetchLimit: 0,
            id: ownerId,
            view: brandingConfig.type,
            includeHidden: includeHidden
        }).$promise
            .then((brandingAssetContainers: JS5Object[]) => {
                _.forEach(brandingAssetContainers, (brandingAssetContainer: JS5Object) => {
                    this.parseBrandingAssetContainer(brandingConfig, brandingAssetContainer);
                });
                return brandingAssetContainers;
            });
    };

    this.createBrandingAssetContainer = (brandingConfig: JS5Object, ownerId: string, brandingAssetContainer: JS5Object) => {
        if (brandingConfig.type === 'ratingOverlays') {
            return $q.reject({
                message: 'Rating overlays use a mock brandingAssetContainer, which cannot be saved to the database'
            });
        }
        let containerOwnerService = BRANDING_ASSET_CONTAINER_OWNERS[brandingConfig.groupName];
        return containerOwnerService.createBrandingAssetContainer({
            id: ownerId,
            view: brandingConfig.type,
        }, brandingAssetContainer).$promise;
    };

    this.toggleBrandingAssetContainerVisibility = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        let containerOwnerService = BRANDING_ASSET_CONTAINER_OWNERS[brandingConfig.groupName];
        return containerOwnerService.toggleBrandingAssetContainerVisibility({
            containerId: brandingAssetContainer.id,
        }).$promise;
    };


    //========================//
    // Shared Implementations //
    //========================//

    this.assetWasIngested = (asset: JS5Object) => {
        return !!_.get(asset, 'assetId');
    };

    this.assetHasProxy = (asset: JS5Object) => {
        let assetDTO = _.get(asset, 'asset', asset);
        return !!_.get(assetDTO, 'proxyId') || _.some(_.get(assetDTO, 'contents'), (assetContent: JS5Object) => {
            return _.includes(assetContent.contentUses, 'PROXY');
        });
    };

    this.getRootAssetRouteId = (containerType: string, rootAsset: JS5Object) => {
        if (containerType === 'watermarks') {
            return _.get(rootAsset, 'videoAcceptProfile.id');
        } else if (containerType === 'ratingOverlays') {
            return _.get(rootAsset, 'mediaAcceptProfile.id');
        }
        return _.get(rootAsset, 'acceptProfile.id');
    };

    this.filterBrandingAssets = (brandingConfig: JS5Object, assets: JS5Object, options: JS5Object = {}) => {
        let filterFunc: JS5Function;
        if (options.anySeasonVolume) {
            // Keep all the containers
            filterFunc = (asset: JS5Object) => true;
        } else if (brandingConfig.seasonId) {
            // For season containers:
            // Only keep assets that belong to this season
            filterFunc = (asset: JS5Object) => (
                asset.seasonId === brandingConfig.seasonId
                && !asset.volumeId
            );
        } else if (brandingConfig.volumeId) {
            // For volume containers:
            // Only keep assets that belong to this volume
            filterFunc = (asset: JS5Object) => (
                asset.volumeId === brandingConfig.volumeId
                && !asset.seasonId
            );
        } else {
            // For non-season & non-volume containers:
            // Only keep assets that don't belong to a season or volume
            filterFunc = (asset: JS5Object) => (
                !asset.seasonId
                && !asset.volumeId
            );
        }
        return _.filter(assets, (asset: JS5Object) => {
            return !!this.getRootAssetRouteId(brandingConfig.type, asset) && filterFunc(asset);
        });
    };
});
